import DOMPurify from "dompurify";

/**
 * Sanitizes HTML content to prevent XSS attacks
 * Allows safe HTML tags and attributes commonly used in rich text editors
 */
export function sanitizeHtml(dirty: string): string {
	if (typeof window === "undefined") {
		// Server-side: return as-is (will be sanitized on client)
		// In a real SSR scenario, you might want to use isomorphic-dompurify
		return dirty;
	}

	return DOMPurify.sanitize(dirty, {
		ALLOWED_TAGS: [
			"p",
			"br",
			"strong",
			"em",
			"u",
			"s",
			"strike",
			"del",
			"h1",
			"h2",
			"h3",
			"h4",
			"h5",
			"h6",
			"ul",
			"ol",
			"li",
			"blockquote",
			"pre",
			"code",
			"a",
			"img",
			"table",
			"thead",
			"tbody",
			"tr",
			"th",
			"td",
			"div",
			"span",
			"iframe", // For embeds like YouTube
			"blockquote", // For Instagram and X embeds
		],
		ALLOWED_ATTR: [
			"href",
			"title",
			"alt",
			"src",
			"width",
			"height",
			"class",
			"id",
			"target",
			"rel",
			"frameborder",
			"allowfullscreen",
			"allow",
			"style", // For inline styles (e.g., from Tiptap)
			"data-type",
			"data-url",
			"data-provider", // For oEmbed nodes
			"data-html",
			"data-width",
			"data-height",
			"data-title",
			"data-thumbnail-url", // For oEmbed stored data
			"data-instgrm-permalink",
			"data-instgrm-version", // For Instagram embeds
			"data-theme", // For X/Twitter embeds
			"sandbox",
			"loading", // For iframe security attributes
		],
		ALLOWED_URI_REGEXP:
			/^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|data):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
		ALLOW_DATA_ATTR: false,
	});
}

/**
 * Checks if HTML content is effectively empty (just whitespace or empty tags)
 * Content with images is considered non-empty even without text
 */
export function isHtmlEmpty(html: string): boolean {
	if (!html || html.trim().length === 0) {
		return true;
	}

	// Check if content has images - if so, it's not empty
	const hasImages = /<img\s[^>]*src=/i.test(html);
	if (hasImages) {
		return false;
	}

	// Check for embedded content (iframes, videos, etc.)
	const hasEmbeds = /<iframe\s/i.test(html) || /<video\s/i.test(html);
	if (hasEmbeds) {
		return false;
	}

	// Remove all HTML tags and check if only whitespace remains
	const textContent = html.replace(/<[^>]*>/g, "").trim();
	return textContent.length === 0;
}
